/**
 * A wrapper around the browser's Storage API that adds support for expirable keys.
 */
export class ExpirableStorage {
  private prefix: string
  private expirationMs: number
  private storage: Storage

  /**
   * Creates an instance of ExpirableStorage.
   * @param storage - The storage mechanism to use (e.g., localStorage or sessionStorage).
   * @param prefix - A prefix to namespace keys in the storage.
   * @param expirationMs - The expiration time for stored items in milliseconds.
   */
  constructor(storage: Storage, prefix: string, expirationMs: number) {
    this.prefix = prefix
    this.expirationMs = expirationMs
    this.storage = storage
  }

  /**
   * Generates a namespaced storage key.
   * @param key - The original key.
   * @returns The namespaced key.
   */
  private storageKey(key: string) {
    return `${this.prefix}:${key}`
  }

  /**
   * Loads a value from storage. If the value is expired or invalid, it is removed.
   * @param key - The key to load.
   * @returns The stored value, or null if it is expired or invalid.
   */
  load(key: string, expirationMs?: number) {
    const age = expirationMs ?? this.expirationMs
    const storageValue = this.storage.getItem(this.storageKey(key)) ?? ''
    try {
      const { value, timestamp } = JSON.parse(storageValue)
      const isExpired = Date.now() - timestamp > age
      if (isExpired) {
        this.storage.removeItem(this.storageKey(key))
        return null
      }
      return value
    } catch {
      this.storage.removeItem(this.storageKey(key))
      return null
    }
  }

  /**
   * Saves a value to storage with a timestamp.
   * @param key - The key to save the value under.
   * @param value - The value to save.
   */
  save(key: string, value: unknown) {
    const data = {
      value: value,
      timestamp: Date.now(),
    }
    this.storage.setItem(this.storageKey(key), JSON.stringify(data))
  }

  /**
   * Removes a value from storage.
   * @param key - The key to remove.
   */
  remove(key: string) {
    this.storage.removeItem(this.storageKey(key))
  }

  /**
   * Lists the raw (already namespaced) storage keys carrying this instance's prefix.
   * Enumerates through the Storage API rather than `Object.keys`, which returns the
   * wrapper's own properties instead of the stored keys.
   */
  keys() {
    const found: string[] = []
    for (let index = 0; index < this.storage.length; index++) {
      const key = this.storage.key(index)
      if (key?.startsWith(this.prefix)) {
        found.push(key)
      }
    }
    return found
  }

  /**
   * Clears all keys with the specified prefix from storage.
   */
  clear() {
    // Collect before removing: removal reindexes the underlying storage.
    for (const key of this.keys()) {
      this.storage.removeItem(key)
    }
  }

  /**
   * Gets the number of keys with the specified prefix in storage.
   * @returns The number of keys.
   */
  get length() {
    return this.keys().length
  }

  /**
   * Gets the key at the specified index among keys with the specified prefix.
   * @param index - The index of the key.
   * @returns The key at the specified index, or null if the index is out of bounds.
   */
  key(index: number) {
    return this.keys()[index] ?? null
  }

  /**
   * Retrieves a value from storage. Alias for `load`.
   * @param key - The key to retrieve.
   * @returns The stored value, or null if it is expired or invalid.
   */
  getItem(key: string) {
    return this.load(key)
  }

  /**
   * Stores a value in storage. Alias for `save`.
   * @param key - The key to store the value under.
   * @param value - The value to store.
   */
  setItem(key: string, value: unknown) {
    this.save(key, value)
  }

  /**
   * Removes a value from storage. Alias for `remove`.
   * @param key - The key to remove.
   */
  removeItem(key: string) {
    this.remove(key)
  }

  /**
   * Clears all keys with the specified prefix from storage. Alias for `clear`.
   */
  clearAll() {
    this.clear()
  }
}
